import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ItemsTable = ({ items, onEdit, onDuplicate, onArchive, onDelete, onBulkEdit, onBulkArchive, onBulkDelete, sortConfig, onSort }) => {
  const [selectedItems, setSelectedItems] = useState([]);

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedItems(items?.map(item => item?.id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (itemId, checked) => {
    if (checked) {
      setSelectedItems([...selectedItems, itemId]);
    } else {
      setSelectedItems(selectedItems?.filter(id => id !== itemId));
    }
  };

  const getSortIcon = (column) => {
    if (sortConfig?.key !== column) return 'ArrowUpDown';
    return sortConfig?.direction === 'asc' ? 'ArrowUp' : 'ArrowDown';
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { bg: 'bg-success/10', text: 'text-success', label: 'Active' },
      inactive: { bg: 'bg-muted', text: 'text-muted-foreground', label: 'Inactive' },
      'out-of-stock': { bg: 'bg-error/10', text: 'text-error', label: 'Out of Stock' }
    };
    
    const config = statusConfig?.[status] || statusConfig?.active;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        {config?.label}
      </span>
    );
  };

  const getStockStatus = (quantity, lowStockThreshold) => {
    if (quantity === 0) return 'out-of-stock';
    if (quantity <= lowStockThreshold) return 'low-stock';
    return 'in-stock';
  };

  const getStockBadge = (quantity, lowStockThreshold) => {
    const status = getStockStatus(quantity, lowStockThreshold);
    const statusConfig = {
      'in-stock': { bg: 'bg-success/10', text: 'text-success', icon: 'CheckCircle' },
      'low-stock': { bg: 'bg-warning/10', text: 'text-warning', icon: 'AlertTriangle' },
      'out-of-stock': { bg: 'bg-error/10', text: 'text-error', icon: 'XCircle' }
    };
    
    const config = statusConfig?.[status];
    return (
      <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${config?.bg} ${config?.text}`}>
        <Icon name={config?.icon} size={12} />
        <span>{quantity}</span>
      </div>
    );
  };

  const handleBulkEdit = () => {
    if (selectedItems?.length > 0) {
      onBulkEdit?.(selectedItems);
      setSelectedItems([]);
    }
  };

  const handleBulkArchive = () => {
    if (selectedItems?.length > 0) {
      onBulkArchive?.(selectedItems);
      setSelectedItems([]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedItems?.length > 0) {
      onBulkDelete?.(selectedItems);
      setSelectedItems([]);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden">
      {/* Table Header */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/30 border-b border-border">
            <tr>
              <th className="w-12 px-4 py-3">
                <input
                  type="checkbox"
                  checked={selectedItems?.length === items?.length && items?.length > 0}
                  onChange={(e) => handleSelectAll(e?.target?.checked)}
                  className="rounded border-border"
                />
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('name')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Item Name</span>
                  <Icon name={getSortIcon('name')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('sku')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>SKU</span>
                  <Icon name={getSortIcon('sku')} size={14} />
                </button>
              </th>
              <th className="text-left px-4 py-3">
                <button
                  onClick={() => onSort('category')}
                  className="flex items-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Category</span>
                  <Icon name={getSortIcon('category')} size={14} />
                </button>
              </th>
              <th className="text-right px-4 py-3">
                <button
                  onClick={() => onSort('price')}
                  className="flex items-center justify-end space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Price</span>
                  <Icon name={getSortIcon('price')} size={14} />
                </button>
              </th>
              <th className="text-center px-4 py-3">
                <button
                  onClick={() => onSort('stock')}
                  className="flex items-center justify-center space-x-2 text-sm font-medium text-foreground hover:text-primary"
                >
                  <span>Stock</span>
                  <Icon name={getSortIcon('stock')} size={14} />
                </button>
              </th>
              <th className="text-center px-4 py-3">
                <span className="text-sm font-medium text-foreground">Status</span>
              </th>
              <th className="text-center px-4 py-3 w-32">
                <span className="text-sm font-medium text-foreground">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {items?.map((item) => (
              <tr key={item?.id} className="hover:bg-muted/20 transition-colors">
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedItems?.includes(item?.id)}
                    onChange={(e) => handleSelectItem(item?.id, e?.target?.checked)}
                    className="rounded border-border"
                  />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                      <Icon name="Package" size={16} className="text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{item?.name}</p>
                      <p className="text-sm text-muted-foreground">{item?.description}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-mono text-foreground">{item?.sku}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-foreground">{item?.category}</span>
                </td>
                <td className="px-4 py-3 text-right">
                  <span className="text-sm font-medium text-foreground">${item?.price?.toFixed(2)}</span>
                </td>
                <td className="px-4 py-3 text-center">
                  {getStockBadge(item?.stock, item?.lowStockThreshold)}
                </td>
                <td className="px-4 py-3 text-center">
                  {getStatusBadge(item?.status)}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center justify-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onEdit(item)}
                      className="h-8 w-8"
                    >
                      <Icon name="Edit" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDuplicate(item)}
                      className="h-8 w-8"
                    >
                      <Icon name="Copy" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onArchive(item)}
                      className="h-8 w-8 text-warning hover:text-warning hover:bg-warning/10"
                    >
                      <Icon name="Archive" size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onDelete?.(item)}
                      className="h-8 w-8 text-error hover:text-error hover:bg-error/10"
                    >
                      <Icon name="Trash2" size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Empty State */}
      {items?.length === 0 && (
        <div className="text-center py-12">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">No items found</h3>
          <p className="text-muted-foreground">Try adjusting your filters or add your first item.</p>
        </div>
      )}
      {/* Bulk Actions Bar */}
      {selectedItems?.length > 0 && (
        <div className="border-t border-border bg-muted/30 px-4 py-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-foreground">
              {selectedItems?.length} item{selectedItems?.length > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center space-x-2">
              <Button variant="outline" size="sm" onClick={handleBulkEdit}>
                <Icon name="Edit" size={14} className="mr-2" />
                Bulk Edit
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkArchive}
                className="text-warning hover:text-warning hover:bg-warning/10"
              >
                <Icon name="Archive" size={14} className="mr-2" />
                Archive Selected
              </Button>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleBulkDelete}
                className="text-error hover:text-error hover:bg-error/10 hover:border-error/20"
              >
                <Icon name="Trash2" size={14} className="mr-2" />
                Delete Selected
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSelectedItems([])}
                className="text-muted-foreground hover:text-foreground"
              >
                <Icon name="X" size={14} className="mr-2" />
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ItemsTable;